import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { getCompatibilitySession, submitPartnerBAnswers } from './services/compatibleApi';
import type { CompatibilityAnswer, CompatibilitySession } from './types/compatible';

const COMPATIBILITY_QUESTIONS = [
  {
    id: 'communication',
    text: 'Comment évaluez-vous votre communication avec votre partenaire ?',
    options: [
      { value: 1, label: 'Difficile' },
      { value: 2, label: 'Moyenne' },
      { value: 3, label: 'Bonne' },
      { value: 4, label: 'Excellente' }
    ]
  },
  // Add more questions
];

const CompatibleRespond: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<CompatibilitySession | null>(null);
  const [answers, setAnswers] = useState<CompatibilityAnswer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSession = async () => {
      if (!sessionId) return;

      try {
        const sessionData = await getCompatibilitySession(sessionId);
        setSession(sessionData);
        
        if (sessionData.partnerBAnswers) {
          navigate(`/compatible/result/${sessionId}`);
        }
      } catch (err) {
        setError('Session non trouvée');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();
  }, [sessionId, navigate]);

  const handleAnswerChange = (questionId: string, value: number) => {
    setAnswers(prev => {
      const existing = prev.find(a => a.questionId === questionId);
      if (existing) {
        return prev.map(a => a.questionId === questionId ? { ...a, value } : a);
      }
      return [...prev, { questionId, value }];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId) return;

    setIsSubmitting(true);
    try {
      await submitPartnerBAnswers(sessionId, answers);
      navigate(`/compatible/result/${sessionId}`);
    } catch (err) {
      setError('Erreur lors de l\'envoi des réponses');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse text-slate-600">Chargement...</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="text-center text-error p-4">
        {error || 'Session non trouvée'}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
      <div className="text-center mb-8">
        <Heart className="mx-auto text-red-500 mb-4" size={48} />
        <h1 className="text-3xl font-bold mb-2">Test de Compatibilité</h1>
        <p className="text-slate-600">
          Votre partenaire vous a invité(e) à répondre à ce test de compatibilité
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {COMPATIBILITY_QUESTIONS.map(question => (
          <div key={question.id} className="card p-6">
            <h3 className="text-lg font-medium mb-4">{question.text}</h3>
            <div className="grid grid-cols-2 gap-3">
              {question.options.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleAnswerChange(question.id, option.value)}
                  className={`p-3 rounded-lg border transition-all ${
                    answers.find(a => a.questionId === question.id && a.value === option.value)
                      ? 'bg-sky-50 border-sky-500 text-sky-700'
                      : 'border-slate-200 hover:border-sky-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ))}

        <button
          type="submit"
          disabled={isSubmitting || answers.length !== COMPATIBILITY_QUESTIONS.length}
          className="btn-primary w-full"
        >
          {isSubmitting ? 'Envoi...' : 'Voir les résultats'}
        </button>
      </form>
    </div>
  );
};

export default CompatibleRespond;